/**
 * Review Panel Component
 *
 * Shows LLM draft output for user confirmation.
 * Supports Accept (green), Edit (yellow), and Discard (red) actions.
 */

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Trash2, X } from "lucide-react";
import { reviewNote } from "../../services/notes";
import type { NoteApiResponse } from "../../types/api";
import { cn } from "../../lib/utils";

interface ReviewPanelProps {
  note: NoteApiResponse;
  onReviewComplete: () => void;
  className?: string;
}

export function ReviewPanel({
  note,
  onReviewComplete,
  className,
}: ReviewPanelProps) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState(
    note.draft_summary_text ?? "",
  );
  const [editedTags, setEditedTags] = useState(note.draft_tags.join(", "));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSavedEdits, setHasSavedEdits] = useState(false);
  // Saved snapshots are committed separately from the in-progress edit
  // fields. Without this split, Cancel-after-second-edit cannot revert
  // editedSummary/editedTags to the last saved values — the "draft"
  // and "saved" roles would share one variable and collide.
  const [savedSummary, setSavedSummary] = useState<string | null>(null);
  const [savedTags, setSavedTags] = useState<string | null>(null);

  const displaySummary = hasSavedEdits
    ? editedSummary
    : (note.draft_summary_text ?? "");
  const displayTags = hasSavedEdits
    ? editedTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
    : note.draft_tags;

  const handleAccept = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      // Accept is the sole API commit point. hasSavedEdits decides
      // whether to submit the edit payload or the untouched draft.
      const updatedNote = hasSavedEdits
        ? await reviewNote(note.id, {
            action: "edit",
            summary_text: editedSummary,
            tags: displayTags,
          })
        : await reviewNote(note.id, { action: "accept" });
      queryClient.setQueryData(["note", note.id], updatedNote);
      onReviewComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => {
    if (!isEditing) {
      setIsEditing(true);
      return;
    }
    // Local save only — snapshot the current edit values so Cancel
    // can revert to them on a future edit round.
    setIsEditing(false);
    setSavedSummary(editedSummary);
    setSavedTags(editedTags);
    setHasSavedEdits(true);
  };

  const handleDiscard = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const updatedNote = await reviewNote(note.id, { action: "discard" });
      queryClient.setQueryData(["note", note.id], updatedNote);
      onReviewComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to discard");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={cn(
        "border border-accent-yellow/30 bg-accent-yellow/5 rounded-lg p-4",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 bg-accent-yellow rounded-full animate-pulse" />
        <h3 className="text-sm font-semibold text-text-primary">
          Review LLM Output
        </h3>
        <span className="text-xs text-text-muted ml-auto">
          Draft awaiting your confirmation
        </span>
      </div>

      {/* Draft Summary */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-text-muted mb-1">
          Summary
        </label>
        {isEditing ? (
          <textarea
            value={editedSummary}
            onChange={(e) => setEditedSummary(e.target.value)}
            className="w-full bg-bg-primary border border-border rounded-md px-3 py-2 text-xs text-text-primary resize-y min-h-[80px] focus:outline-hidden focus:ring-1 focus:ring-accent-yellow"
            rows={4}
          />
        ) : (
          <div className="bg-bg-primary border border-border rounded-md px-3 py-2 text-xs text-text-primary whitespace-pre-wrap">
            {displaySummary || "No summary generated"}
          </div>
        )}
      </div>

      {/* Draft Tags */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-text-muted mb-1">
          Tags
        </label>
        {isEditing ? (
          <input
            type="text"
            value={editedTags}
            onChange={(e) => setEditedTags(e.target.value)}
            className="w-full bg-bg-primary border border-border rounded-md px-3 py-1.5 text-xs text-text-primary focus:outline-hidden focus:ring-1 focus:ring-accent-yellow"
            placeholder="tag1, tag2, tag3"
          />
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {displayTags.length > 0 ? (
              displayTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-accent-blue/15 text-accent-blue"
                >
                  {tag}
                </span>
              ))
            ) : (
              <span className="text-xs text-text-muted">No tags generated</span>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-3 text-xs text-accent-red bg-accent-red/10 border border-accent-red/20 rounded-sm px-3 py-2">
          {error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleAccept}
          disabled={isSubmitting || isEditing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-green/15 text-accent-green rounded-md text-xs font-medium hover:bg-accent-green/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Check className="w-3.5 h-3.5" />
          Accept
        </button>

        <button
          type="button"
          onClick={handleEdit}
          disabled={isSubmitting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-yellow/15 text-accent-yellow rounded-md text-xs font-medium hover:bg-accent-yellow/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
          {isEditing ? "Save Edits" : "Edit"}
        </button>

        {isEditing && (
          <button
            type="button"
            onClick={() => {
              setIsEditing(false);
              // Revert the in-progress edit to the last saved snapshot
              // (or to the server draft if no save has happened yet).
              // Without this, a second edit round that's then cancelled
              // would silently leak the unsaved text into displaySummary.
              if (hasSavedEdits) {
                setEditedSummary(savedSummary ?? "");
                setEditedTags(savedTags ?? "");
              } else {
                setEditedSummary(note.draft_summary_text ?? "");
                setEditedTags(note.draft_tags.join(", "));
              }
            }}
            disabled={isSubmitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-bg-hover text-text-muted rounded-md text-xs font-medium hover:bg-border hover:text-text-primary disabled:opacity-50 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Cancel
          </button>
        )}

        <button
          type="button"
          onClick={handleDiscard}
          disabled={isSubmitting || isEditing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-red/15 text-accent-red rounded-md text-xs font-medium hover:bg-accent-red/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ml-auto"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Discard
        </button>
      </div>
    </div>
  );
}
